import { Grid } from '@material-ui/core';
import { Card, Typography } from '@nutrien/cxp-components';
import dayjs from 'dayjs';
import React, { useEffect, useState } from 'react';

import { useMst } from '../../mobx-models/Root';
import { FileCategory } from '../../models/models';
import type { SignAndStampFile } from '../../rxdb/signAndStamp/signAndStamp.types';
import { YYYYMMDD_DATE_FORMAT } from '../../utilities/constants';
import signAndStampStyles from '../SignAndStamp/SignAndStamp.styles';
import SignAndStampImageView from '../SignAndStampImageView';

interface Props {
  data?: SignAndStampFile[];
}

const SignAndStampCompletedView: React.FC<Props> = ({ data }) => {
  const classes = signAndStampStyles();
  const { CurrentUserSelected } = useMst();
  const [taskSignData, setTaskSignData] = useState<SignAndStampFile>();
  const [taskStampData, setTaskStampData] = useState<SignAndStampFile>();
  const [inspectionSignData, setInspectionSignData] = useState<SignAndStampFile>();
  const [inspectionStampData, setInspectionStampData] = useState<SignAndStampFile>();

  useEffect(() => {
    if (data) {
      setTaskSignData(
        data.find(
          (item: SignAndStampFile) =>
            item.fileCategory === FileCategory.Sign &&
            item.fkAssetInspectionTask === CurrentUserSelected.taskId &&
            !item.userId,
        ),
      );
      setTaskStampData(
        data.find(
          (item: SignAndStampFile) =>
            item.fileCategory === FileCategory.Stamp &&
            item.fkAssetInspectionTask === CurrentUserSelected.taskId &&
            !item.userId,
        ),
      );
      setInspectionSignData(
        data.find(
          (item: SignAndStampFile) =>
            item.fileCategory === FileCategory.Sign &&
            Number(item.inspectionId) === CurrentUserSelected.inspectionId &&
            !item.userId &&
            item.fkAssetInspectionTask === 0,
        ),
      );
      setInspectionStampData(
        data.find(
          (item: SignAndStampFile) =>
            item.fileCategory === FileCategory.Stamp &&
            Number(item.inspectionId) === CurrentUserSelected.inspectionId &&
            !item.userId &&
            item.fkAssetInspectionTask === 0,
        ),
      );
    }
  }, [data]);

  return (
    <Grid className={classes.signAndStampCompletedView}>
      <Card elevation={1} className="signAndStampCard">
        <Grid xs={12} container>
          <Grid item className={classes.titleGrid}>
            <Typography variant="h3" className="title">
              Signature and Stamping
            </Typography>
          </Grid>
        </Grid>
        <Grid xs={12} container className={classes.fiLiViewContainer}>
          {(taskSignData || taskStampData) && (
            <Grid item xs={6}>
              <Typography variant="h4" className="fiLiTitle">
                Field Inspector
              </Typography>
              <Grid item xs={12} className={classes.flexRow}>
                <Grid item xs={4}>
                  <Typography variant="subtitle1" className="fiLiSubtitle">
                    Print Name
                  </Typography>
                </Grid>
                <Grid item xs={8}>
                  <Typography
                    variant="subtitle1"
                    className={classes.fiLiSubtitleLeft}
                    data-testid="field-inspector-name"
                  >
                    {taskSignData?.fieldInspectorName || '-'}
                  </Typography>
                </Grid>
              </Grid>
              <Grid item xs={12} className={classes.flexRow}>
                <Grid item xs={4}>
                  <Typography variant="subtitle1" className="fiLiSubtitle">
                    Title
                  </Typography>
                </Grid>
                <Grid item xs={8}>
                  <Typography variant="subtitle1" className={classes.fiLiSubtitleLeft}>
                    {taskSignData?.fieldInspectorTitle || '-'}
                  </Typography>
                </Grid>
              </Grid>
              <Grid item xs={12} className={classes.flexRow}>
                <Grid item xs={4}>
                  <Typography variant="subtitle1" className="fiLiSubtitle">
                    Designation
                  </Typography>
                </Grid>
                <Grid item xs={8}>
                  <Typography variant="subtitle1" className={classes.fiLiSubtitleLeft}>
                    {taskSignData?.fieldDesignation || '-'}
                  </Typography>
                </Grid>
              </Grid>
              <Grid item xs={12} className={classes.flexRow}>
                <Grid item xs={4}>
                  <Typography variant="subtitle1" className="fiLiSubtitle">
                    Company
                  </Typography>
                </Grid>
                <Grid item xs={8}>
                  <Typography variant="subtitle1" className={classes.fiLiSubtitleLeft}>
                    {taskSignData?.fieldCompanyName || '-'}
                  </Typography>
                </Grid>
              </Grid>
              <Grid item xs={12} className={classes.flexRow}>
                <Grid item xs={4}>
                  <Typography variant="subtitle1" className="fiLiSubtitle">
                    Date Signed
                  </Typography>
                </Grid>
                <Grid item xs={8}>
                  <Typography variant="subtitle1" className={classes.fiLiSubtitleLeft}>
                    {taskSignData?.dateCreated
                      ? dayjs(taskSignData?.dateCreated).format(`${YYYYMMDD_DATE_FORMAT}`)
                      : '-'}
                  </Typography>
                </Grid>
              </Grid>
              <Grid item xs={12} className={`${classes.flexRow} ${classes.imageViewGrid}`}>
                <Grid item xs={5}>
                  {taskSignData && <SignAndStampImageView data={taskSignData} altText="Sign" />}
                </Grid>
                <Grid item xs={7}>
                  {taskStampData && <SignAndStampImageView data={taskStampData} altText="Stamp" />}
                </Grid>
              </Grid>
            </Grid>
          )}
          {(inspectionSignData || inspectionStampData) && (
            <Grid item xs={6}>
              <Typography variant="h4" className="fiLiTitle">
                Lead Inspector
              </Typography>
              <Grid item xs={12} className={classes.flexRow}>
                <Grid item xs={4}>
                  <Typography variant="subtitle1" className="fiLiSubtitle">
                    Print Name
                  </Typography>
                </Grid>
                <Grid item xs={8}>
                  <Typography
                    variant="subtitle1"
                    className={classes.fiLiSubtitleLeft}
                    data-testid="lead-inspector-name"
                  >
                    {inspectionSignData?.leadInspectorName || '-'}
                  </Typography>
                </Grid>
              </Grid>
              <Grid item xs={12} className={classes.flexRow}>
                <Grid item xs={4}>
                  <Typography variant="subtitle1" className="fiLiSubtitle">
                    Title
                  </Typography>
                </Grid>
                <Grid item xs={8}>
                  <Typography variant="subtitle1" className={classes.fiLiSubtitleLeft}>
                    {inspectionSignData?.leadInspectorTitle || '-'}
                  </Typography>
                </Grid>
              </Grid>
              <Grid item xs={12} className={classes.flexRow}>
                <Grid item xs={4}>
                  <Typography variant="subtitle1" className="fiLiSubtitle">
                    Designation
                  </Typography>
                </Grid>
                <Grid item xs={8}>
                  <Typography variant="subtitle1" className={classes.fiLiSubtitleLeft}>
                    {inspectionSignData?.leadDesignation || '-'}
                  </Typography>
                </Grid>
              </Grid>
              <Grid item xs={12} className={classes.flexRow}>
                <Grid item xs={4}>
                  <Typography variant="subtitle1" className="fiLiSubtitle">
                    Company
                  </Typography>
                </Grid>
                <Grid item xs={8}>
                  <Typography variant="subtitle1" className={classes.fiLiSubtitleLeft}>
                    {inspectionSignData?.leadCompanyName || '-'}
                  </Typography>
                </Grid>
              </Grid>
              <Grid item xs={12} className={classes.flexRow}>
                <Grid item xs={4}>
                  <Typography variant="subtitle1" className="fiLiSubtitle">
                    Date Signed
                  </Typography>
                </Grid>
                <Grid item xs={8}>
                  <Typography variant="subtitle1" className={classes.fiLiSubtitleLeft}>
                    {inspectionSignData?.dateCreated
                      ? dayjs(inspectionSignData?.dateCreated).format(`${YYYYMMDD_DATE_FORMAT}`)
                      : '-'}
                  </Typography>
                </Grid>
              </Grid>
              <Grid item xs={12} className={`${classes.flexRow} ${classes.imageViewGrid}`}>
                <Grid item xs={5}>
                  {inspectionSignData && (
                    <SignAndStampImageView data={inspectionSignData} altText="Sign" />
                  )}
                </Grid>
                <Grid item xs={7}>
                  {inspectionStampData && (
                    <SignAndStampImageView data={inspectionStampData} altText="Stamp" />
                  )}
                </Grid>
              </Grid>
            </Grid>
          )}
        </Grid>
      </Card>
    </Grid>
  );
};

export default SignAndStampCompletedView;
